<script setup lang="ts">
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { computed, ref } from 'vue';
import VRadioField from '@shared/form/VRadioField/VRadioField.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useSamenwerkingsovereenkomstOverzicht } from '@services/documenten-internal/orval-api/samenwerkingsovereenkomst/samenwerkingsovereenkomst';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import { useBreadcrumb } from '@menu-lib/breadcrumb/breadcrumb';

const beurs = ref<boolean>(false);

const adviseurDossierHref = (adviseurnummer: number) =>
  `/app/crm/adviseurs/${adviseurnummer}/dossier`;

useBreadcrumb([
  {
    link: '/app/crm/adviseurs',
    name: 'Adviseurbeheer',
  },
  { link: '', name: 'Samenwerkingsovereenkomst overzicht' },
]);

const { data, isPending } = useSamenwerkingsovereenkomstOverzicht(
  computed(() => ({ beurs: beurs.value })),
  {
    query: {
      select: (r) =>
        r.result?.map((row) => ({
          ...row,
          verstuurd: row.verstuurd ? 'Ja' : 'Nee',
          ondertekend: row.ondertekend ? 'Ja' : 'Nee',
          beursBeschikbaar: row.beursBeschikbaar ? 'Ja' : 'Nee',
          accountmanager: row.accountmanager ? row.accountmanager : '(geen)',
        })),
    },
  },
);

const verstuurd = computed(() => data.value?.filter((d) => d.verstuurd === 'Ja')?.length);
const ondertekend = computed(
  () => data.value?.filter((d) => d.verstuurd === 'Ja' && d.ondertekend === 'Ja')?.length,
);
const nietOndertekend = computed(
  () => data.value?.filter((d) => d.verstuurd === 'Ja' && d.ondertekend === 'Nee')?.length,
);
</script>
<template>
  <VPageContainer title="Samenwerkingsovereenkomst overzicht">
    <div class="flex flex-col gap-4">
      <VRadioField
        name="beurs"
        v-model:value="beurs"
        :options="[
          { label: 'Volmacht', value: false },
          { label: 'Beurs', value: true },
        ]"
      />
      <VSkeleton v-if="isPending" height="lg" :count="20" />
      <div v-else>
        <div class="mb-4 flex flex-row gap-2">
          <VStatusTag inverted color="grey">{{ verstuurd }} verstuurd</VStatusTag>
          <VStatusTag inverted color="green">waarvan {{ ondertekend }} ondertekend</VStatusTag>
          <VStatusTag inverted color="red">
            waarvan {{ nietOndertekend }} niet ondertekend
          </VStatusTag>
        </div>
        <VDataTable
          current-page-report-template="van {totalPages} ({totalRecords})"
          :global-filter-fields="[
            'naamAdviseur',
            'accountmanager',
            'adviseurnummer',
            'verstuurdNaar',
          ]"
          state-storage="session"
          state-key="swoOverzichtTable"
          :filterable-properties="
            [
              { field: 'verstuurd', header: 'Verstuurd' },
              { field: 'ondertekend', header: 'Ondertekend' },
              { field: 'accountmanager', header: 'Accountmanager' },
              { field: 'beursBeschikbaar', header: 'Beurs toegang' },
            ] as any
          "
          searchable
          exportable
          :value="data"
        >
          <VColumn sortable header="Adviseur" field="adviseurnummer">
            <template #body="{ data }">
              <a
                :href="adviseurDossierHref(data.adviseurnummer)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ data.adviseurnummer }} - {{ data.naamAdviseur }}
              </a>
            </template>
          </VColumn>
          <VColumn header="Verstuurd" field="verstuurdOp">
            <template #body="{ data }">
              <div v-if="data.ondertekend === 'Ja' && data.betreftHandmatig === true">
                <!-- show nothing -->
              </div>
              <div v-else-if="data.verstuurd === 'Ja'">
                <FontAwesomeIcon icon="far fa-check" class="text-green" />
                {{ data.verstuurdOp }} - {{ data.verstuurdNaar }}
              </div>
              <div v-else>
                <FontAwesomeIcon icon="far fa-xmark" class="text-red" />
              </div>
            </template>
          </VColumn>
          <VColumn header="Ondertekend" field="ondertekendOp">
            <template #body="{ data }">
              <div v-if="data.ondertekend === 'Ja'">
                <FontAwesomeIcon icon="far fa-check" class="text-green" />
                {{ data.ondertekendOp }}
              </div>
              <div v-else>
                <FontAwesomeIcon icon="far fa-xmark" class="text-red" />
              </div>
            </template>
          </VColumn>
          <VColumn header="Accountmanager" field="accountmanager" />
          <VColumn header="Beurs toegang" field="beursBeschikbaar" v-if="beurs">
            <template #body="{ data }">
              <FontAwesomeIcon
                v-if="data.beursBeschikbaar === 'Ja'"
                icon="far fa-check"
                class="text-green"
              />
              <FontAwesomeIcon v-else icon="far fa-xmark" class="text-red" />
            </template>
          </VColumn>
        </VDataTable>
      </div>
    </div>
  </VPageContainer>
</template>
